// CB:BEGIN admin-help-page
/**
 * AdminHelpPage — CoreBids Admin Operations Guide
 *
 * A searchable, structured help center for admin operators.
 * Covers: dashboard, projects, milestones, verification, payments, health system, activity.
 */
import React, { useState, useMemo } from 'react';
import {
  BookOpen,
  Search,
  Briefcase,
  Users,
  ShieldCheck,
  DollarSign,
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

interface HelpSection {
  id: string;
  title: string;
  icon: React.ElementType;
  articles: HelpArticle[];
}

interface HelpArticle {
  id: string;
  title: string;
  content: React.ReactNode;
  keywords: string[];
}

// ── Content ─────────────────────────────────────────────────────────────────

const HELP_SECTIONS: HelpSection[] = [
  {
    id: 'dashboard',
    title: 'Dashboard Overview',
    icon: Activity,
    articles: [
      {
        id: 'dashboard-overview',
        title: 'How the admin dashboard works',
        keywords: ['dashboard', 'overview', 'war room', 'operations'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>The admin dashboard is an Operations Control Center. It shows the state of the entire platform at a glance.</p>
            <p><strong>Action Required queue</strong> — at the top of the dashboard. Lists every project that has been inactive for more than 72 hours. These projects may need an admin nudge to get moving. Click "Review" to open the project.</p>
            <p><strong>Project Health summary</strong> — four tiles showing counts of healthy projects, projects waiting on homeowner action, projects waiting on contractor action, and projects that need admin attention. Click any tile to open the filtered projects list.</p>
            <p><strong>Platform Overview</strong> — total users, total projects, bids accepted, and platform revenue. Click any tile to drill into the relevant area.</p>
            <p><strong>Quick Navigation</strong> — links to every major admin section. No more hunting for pages.</p>
          </div>
        ),
      },
      {
        id: 'action-required',
        title: 'Understanding the Action Required queue',
        keywords: ['action required', 'stalled', 'alerts', 'queue'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>The Action Required queue shows projects that need admin attention. A project appears here when:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>The project has not had any status update for more than 72 hours</li>
              <li>A dispute has been reported</li>
              <li>A milestone has been waiting for approval for more than 48 hours</li>
            </ul>
            <p>For each item, you can see the project address, current status, and how many days it has been inactive. Click "Review" to open the project detail page (ProjectCockpit) where you can take action: send a reminder, force a status transition, or escalate.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'projects',
    title: 'Project Management',
    icon: Briefcase,
    articles: [
      {
        id: 'searching-projects',
        title: 'How to search for a project',
        keywords: ['search', 'find project', 'filter'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>Open Projects from the navigation. Use the search bar to find by:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Project address or city</li>
              <li>Homeowner name or email</li>
            </ul>
            <p>Use the status filter to narrow by project phase (Draft, Bidding, Active, Completed, etc.).</p>
            <p>Use the health filter to show only projects in a specific health state (Needs Attention, Waiting on Homeowner, etc.).</p>
          </div>
        ),
      },
      {
        id: 'project-status',
        title: 'Project status lifecycle',
        keywords: ['status', 'lifecycle', 'phases', 'state'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>Projects move through the following phases:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 border border-gray-200">Status</th>
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 border border-gray-200">Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['DRAFT', 'Project created but not submitted'],
                    ['BIDS_COLLECTING', 'Contractors are bidding'],
                    ['BIDS_READY', 'Bids received — homeowner needs to choose'],
                    ['DECIDING', 'Homeowner reviewing bids'],
                    ['HOMEOWNER_CONTRACT_SIGNING', 'Homeowner must sign the agreement'],
                    ['ESCROW_PENDING', 'Homeowner must fund milestone escrow'],
                    ['BID_SELECTED', 'Contractor confirmed, project starting'],
                    ['SCHEDULED', 'Start date agreed, work not yet begun'],
                    ['ACTIVE_P1 / P2 / P3', 'Milestone work in progress'],
                    ['IN_PROGRESS', 'General active state'],
                    ['COMPLETED', 'All milestones approved, project finished'],
                    ['DISPUTED', 'Issue raised — requires admin'],
                  ].map(([s, m]) => (
                    <tr key={s} className="border-b border-gray-100">
                      <td className="px-3 py-2 font-mono text-[11px] text-gray-800 border border-gray-200 bg-gray-50">{s}</td>
                      <td className="px-3 py-2 text-gray-700 border border-gray-200">{m}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ),
      },
      {
        id: 'view-as-user',
        title: 'View project as homeowner or contractor',
        keywords: ['view as', 'homeowner view', 'contractor view', 'user perspective'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>Inside the project detail page (ProjectCockpit), you can view the project exactly as the homeowner or contractor sees it. This is the fastest way to diagnose user confusion or reproduce a reported issue.</p>
            <p>Look for the "View As Homeowner" and "View As Contractor" buttons at the top of the project detail panel. These open the user's exact dashboard view in context of that project.</p>
          </div>
        ),
      },
      {
        id: 'force-transition',
        title: 'Forcing a project status transition',
        keywords: ['force', 'transition', 'override', 'status change', 'admin override'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>In rare cases, a project may be stuck in a status it cannot leave through normal workflow (e.g. a bug, a user who never responds). Admins can force a status transition from the ProjectCockpit.</p>
            <p>Open the project, scroll to "Admin Quick Actions", enter a justification in the notes field, and select the target status. All forced transitions are logged in the audit trail.</p>
            <p><strong>Only force transitions when there is a clear operational reason.</strong> Never force a project to a status that skips payment or legal steps without coordination.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'milestones',
    title: 'Milestones',
    icon: CheckCircle2,
    articles: [
      {
        id: 'milestone-overview',
        title: 'How milestone submissions work',
        keywords: ['milestone', 'evidence', 'submission', 'approval'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>Projects are split into three milestones (P1, P2, P3). Each milestone:</p>
            <ol className="list-decimal list-inside space-y-1 pl-2">
              <li>Contractor uploads evidence (photos, notes) via their dashboard</li>
              <li>Homeowner reviews and approves or disputes</li>
              <li>Upon approval, the escrow payment is released to the contractor</li>
            </ol>
            <p>Admins can view milestone evidence and approval status in the project detail page under the Milestones section.</p>
          </div>
        ),
      },
      {
        id: 'milestone-stalled',
        title: 'When should admins intervene in milestones?',
        keywords: ['intervene', 'stalled milestone', 'milestone dispute'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>Intervene when:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>A milestone has been submitted but not approved for more than 48 hours — send a reminder to the homeowner</li>
              <li>A homeowner disputes a milestone — review the evidence and mediate</li>
              <li>A contractor has not submitted milestone evidence 5+ days after the agreed start date — contact the contractor</li>
            </ul>
            <p>Use "Send Reminder" in Admin Quick Actions to notify the responsible party automatically.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'verification',
    title: 'Contractor Verification',
    icon: ShieldCheck,
    articles: [
      {
        id: 'verification-overview',
        title: 'How contractor verification works',
        keywords: ['verification', 'contractor', 'license', 'insurance', 'documents'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>Before a contractor can bid on projects, they must be verified. The verification process requires:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Business license upload</li>
              <li>General liability insurance certificate</li>
              <li>Workers compensation certificate (or waiver)</li>
              <li>W-9 tax form</li>
              <li>Signed CoreBids contractor agreement</li>
            </ul>
            <p>Contractors submit these documents through their onboarding flow. They appear in the Verification Queue once all required documents are uploaded.</p>
          </div>
        ),
      },
      {
        id: 'verification-review',
        title: 'How to approve or reject a contractor',
        keywords: ['approve contractor', 'reject contractor', 'verify', 'review documents'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>Open Verification Queue from the navigation. Each submission shows the contractor's name, business, and uploaded documents.</p>
            <p><strong>To approve:</strong> Review each document. If all documents are valid and not expired, click Approve. The contractor is immediately eligible to bid on projects.</p>
            <p><strong>To reject:</strong> Click Reject and provide a reason. The contractor is notified and can resubmit corrected documents.</p>
            <p><strong>To request more information:</strong> Use the admin notes field to log what is missing, then notify the contractor via email or through the platform reminder system.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'payments',
    title: 'Payments & Escrow',
    icon: DollarSign,
    articles: [
      {
        id: 'escrow-overview',
        title: 'How escrow works',
        keywords: ['escrow', 'payment', 'stripe', 'payout', 'funds'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>CoreBids uses milestone-based escrow. Here is the flow:</p>
            <ol className="list-decimal list-inside space-y-1 pl-2">
              <li>Homeowner funds escrow via Stripe (total project amount split into milestones)</li>
              <li>Funds are held in the platform's Stripe account</li>
              <li>When a homeowner approves a milestone, the milestone payment is released to the contractor's connected Stripe account</li>
              <li>The platform fee is deducted before the payout</li>
            </ol>
            <p>Access escrow details in Finance from the navigation.</p>
          </div>
        ),
      },
      {
        id: 'payment-issues',
        title: 'How to identify payment issues',
        keywords: ['payment failed', 'payout error', 'stripe error', 'failed transaction'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>Payment issues appear in the Finance section. Common issues:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li><strong>Contractor missing Stripe setup</strong> — contractor has not connected their Stripe account. They cannot receive payouts. Ask them to complete onboarding.</li>
              <li><strong>Failed deposit</strong> — homeowner card declined. The homeowner needs to update their payment method.</li>
              <li><strong>Payout hold</strong> — Stripe has placed the contractor's account under review. Contact Stripe support.</li>
            </ul>
          </div>
        ),
      },
    ],
  },
  {
    id: 'health',
    title: 'Project Health System',
    icon: Activity,
    articles: [
      {
        id: 'health-definitions',
        title: 'Health status definitions',
        keywords: ['health', 'status', 'healthy', 'waiting', 'attention'],
        content: (
          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-green-500 flex-shrink-0" />
              <div>
                <div className="font-semibold text-green-800">Healthy</div>
                <p className="text-green-700 mt-1">Project is progressing normally. No action needed. The project has had recent activity and is in an expected state.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-amber-500 flex-shrink-0" />
              <div>
                <div className="font-semibold text-amber-800">Waiting on Homeowner</div>
                <p className="text-amber-700 mt-1">The homeowner must take the next action. Project status is one of: bids ready for review, deciding, awaiting contract signature, or awaiting escrow funding. If waiting exceeds 72 hours, escalates to Needs Attention.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-amber-500 flex-shrink-0" />
              <div>
                <div className="font-semibold text-amber-800">Waiting on Contractor</div>
                <p className="text-amber-700 mt-1">The contractor must take the next action. Project is in bidding, bid selected, or scheduled state. If waiting exceeds 72 hours, escalates to Needs Attention.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
              <span className="w-2 h-2 mt-1.5 rounded-full bg-red-500 flex-shrink-0" />
              <div>
                <div className="font-semibold text-red-800">Needs Attention</div>
                <p className="text-red-700 mt-1">Project is stalled or has a problem. Triggers: no activity for 72+ hours, dispute reported, or contractor missing setup. These appear in the Action Required queue. Admins should investigate.</p>
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'health-response',
        title: 'How admins should respond to each health state',
        keywords: ['respond', 'admin action', 'health response', 'stalled'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p><strong>Waiting on Homeowner (recent)</strong> — no action needed. The workflow is working. Check back in 24-48 hours.</p>
            <p><strong>Waiting on Homeowner (72h+)</strong> — send a reminder via Admin Quick Actions in the project detail. The homeowner may have forgotten or missed the notification.</p>
            <p><strong>Waiting on Contractor (72h+)</strong> — contact the contractor directly. They may have availability issues or need to update their Stripe setup.</p>
            <p><strong>Needs Attention / Disputed</strong> — open the project, review the issue notes, contact both parties if needed, and add an admin note documenting your review. Use force-transition only if absolutely necessary.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'activity',
    title: 'Activity Log',
    icon: Clock,
    articles: [
      {
        id: 'activity-overview',
        title: 'How the activity log works',
        keywords: ['activity', 'log', 'audit', 'history', 'events'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p>Every significant event on the platform is logged in the audit trail. This includes:</p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Project created, status changed</li>
              <li>Bid submitted, accepted</li>
              <li>Milestone submitted, approved</li>
              <li>Contractor verified, rejected</li>
              <li>Stripe onboarding status changed</li>
              <li>Admin override actions with justification</li>
            </ul>
            <p>Access the full audit log via Operations from the navigation. You can search by project ID, user, or action type.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: 'search',
    title: 'Search & Filters',
    icon: Search,
    articles: [
      {
        id: 'search-guide',
        title: 'Search and filter reference',
        keywords: ['search', 'filter', 'find', 'project id', 'user search'],
        content: (
          <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
            <p><strong>Project search:</strong> Search by address, city, homeowner name or email in the Projects page.</p>
            <p><strong>User search:</strong> Search homeowners by name/email in Homeowners. Search contractors by company/email in Contractors.</p>
            <p><strong>Project filters:</strong></p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Status — filter to a specific lifecycle phase</li>
              <li>Health — filter to Healthy / Waiting on Homeowner / Waiting on Contractor / Needs Attention</li>
              <li>Project type — Roof, Siding, Gutters, Windows</li>
            </ul>
            <p><strong>Finding stalled projects:</strong> Use the Action Required queue on the dashboard, or filter by health "Needs Attention" in the Projects page.</p>
          </div>
        ),
      },
    ],
  },
];

// ── Main Component ──────────────────────────────────────────────────────────

const AdminHelpPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['dashboard']));
  const [expandedArticles, setExpandedArticles] = useState<Set<string>>(new Set(['dashboard-overview']));

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleArticle = (id: string) => {
    setExpandedArticles((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredSections = useMemo(() => {
    if (!query.trim()) return HELP_SECTIONS;
    const q = query.toLowerCase();
    return HELP_SECTIONS.map((section) => ({
      ...section,
      articles: section.articles.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.keywords.some((k) => k.includes(q)),
      ),
    })).filter((s) => s.articles.length > 0);
  }, [query]);

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#f0faf9] flex items-center justify-center">
          <BookOpen className="w-5 h-5 text-[#123c3b]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Operations Guide</h1>
          <p className="text-sm text-gray-500">Reference for platform administrators</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search topics — try 'milestone', 'verification', 'payments'..."
          className="w-full h-10 pl-9 pr-4 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#123c3b]/20 focus:border-[#123c3b] placeholder:text-gray-400"
        />
      </div>

      {/* Sections */}
      <div className="space-y-3">
        {filteredSections.map((section) => {
          const Icon = section.icon;
          const isOpen = expandedSections.has(section.id) || query.length > 0;

          return (
            <div key={section.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Section header */}
              <button
                type="button"
                onClick={() => toggleSection(section.id)}
                className="flex items-center gap-3 w-full px-5 py-4 text-left hover:bg-gray-50 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-[#f0faf9] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#123c3b]" />
                </div>
                <span className="text-base font-semibold text-gray-900 flex-1">{section.title}</span>
                <span className="text-xs text-gray-400 mr-2">{section.articles.length} articles</span>
                {isOpen
                  ? <ChevronDown className="w-4 h-4 text-gray-400" />
                  : <ChevronRight className="w-4 h-4 text-gray-400" />
                }
              </button>

              {/* Articles */}
              {isOpen && (
                <div className="border-t border-gray-100">
                  {section.articles.map((article, i) => {
                    const isArticleOpen = expandedArticles.has(article.id) || query.length > 0;

                    return (
                      <div
                        key={article.id}
                        className={i < section.articles.length - 1 ? 'border-b border-gray-100' : ''}
                      >
                        <button
                          type="button"
                          onClick={() => toggleArticle(article.id)}
                          className="flex items-center gap-2 w-full px-5 py-3 text-left hover:bg-gray-50 transition-colors"
                        >
                          {isArticleOpen
                            ? <ChevronDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            : <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          }
                          <span className="text-sm font-medium text-gray-800">{article.title}</span>
                        </button>

                        {isArticleOpen && (
                          <div className="px-5 pb-4">
                            {article.content}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {filteredSections.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Search className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No articles found for "{query}"</p>
            <p className="text-xs mt-1 opacity-70">Try a different keyword</p>
          </div>
        )}
      </div>

      {/* Footer note */}
      <div className="mt-8 p-4 bg-gray-50 rounded-xl border border-gray-200 text-xs text-gray-500">
        This guide covers the CoreBids admin operations. For platform architecture and engineering details, see
        {' '}<code className="font-mono bg-gray-100 px-1 rounded">docs/admin-operations-guide.md</code>{' '}
        in the repository.
      </div>
    </div>
  );
};

export default AdminHelpPage;
// CB:END admin-help-page
